/**
 * The stretched terrain-following vertical grid.
 *
 * Coordinate is ζ = z − h(x, y), height above ground. Layer thickness is
 * independent of x and y, so the Jacobian is constant and the AGL bands the UI
 * asks for (10/30/50/100/200/500 m) are a plain vertical interpolation.
 */

import type { VerticalGrid } from './types';

/**
 * The lowest height-above-ground band the UI is allowed to display.
 *
 * This is a physics constraint, not a UI preference: sampling a band means
 * interpolating between cell centres, and the log-law profile is steeply
 * concave near the ground, so linear interpolation across a gap that straddles
 * the band underestimates the wind. `firstLayerThickness` sizes the grid so at
 * least two cell centres sit below this height.
 */
export const LOWEST_RESOLVED_AGL = 10;

/**
 * Thickness of the first layer, chosen so `LOWEST_RESOLVED_AGL` is bracketed by
 * cell centres rather than extrapolated to.
 *
 * With geometric stretching the second centre sits at roughly `1.6 · dz0`, so
 * keeping `dz0` under about half the lowest band puts two centres beneath it.
 * The cost is a slightly larger stretch ratio and therefore a thicker top
 * layer, which is harmless — the top of the domain is where nothing
 * interesting happens, and the vertical line smoother solves each column
 * exactly regardless of how stretched it is.
 *
 * The absolute floor is well below the resolution cap on purpose: a floor that
 * sits above the cap silently defeats it, which is what happened when the
 * lowest band dropped to 10 m while the floor was still 8.
 */
export function firstLayerThickness(top: number, nz: number): number {
  const resolutionCap = LOWEST_RESOLVED_AGL / 2;
  return Math.max(2.5, Math.min(resolutionCap, top / 150, top / nz));
}

/**
 * Geometric stretching: dz_k = dz0 · r^k, summing to `top`. The ratio is found
 * by bisection because there is no closed form for r given (dz0, nz, top).
 */
export function buildVerticalGrid(nz: number, top: number, firstLayer: number): VerticalGrid {
  const dz0 = Math.min(firstLayer, top / nz);

  const total = (r: number) => (Math.abs(r - 1) < 1e-9 ? dz0 * nz : (dz0 * (Math.pow(r, nz) - 1)) / (r - 1));

  let lo = 1.0;
  let hi = 2.0;
  if (total(hi) < top) hi = 4.0;
  for (let it = 0; it < 80; it++) {
    const mid = 0.5 * (lo + hi);
    if (total(mid) < top) lo = mid;
    else hi = mid;
  }
  const r = 0.5 * (lo + hi);

  const dz = new Float32Array(nz);
  const zf = new Float32Array(nz + 1);
  const zc = new Float32Array(nz);
  let acc = 0;
  for (let k = 0; k < nz; k++) {
    dz[k] = dz0 * Math.pow(r, k);
    zf[k] = acc;
    zc[k] = acc + dz[k] / 2;
    acc += dz[k];
  }
  zf[nz] = acc;

  // dzc[k] spans centre k to centre k+1. The last entry is the spacing to the
  // Dirichlet ghost above the top, taken as a full final layer.
  const dzc = new Float32Array(nz);
  for (let k = 0; k < nz - 1; k++) dzc[k] = 0.5 * (dz[k] + dz[k + 1]);
  dzc[nz - 1] = dz[nz - 1];

  return { nz, dz, zc, zf, dzc, top: acc };
}

/** Fractional layer index for a height above ground, for sampling AGL bands. */
export function layerIndexAt(vg: VerticalGrid, heightAGL: number): { k0: number; k1: number; t: number } {
  const { zc, nz } = vg;
  if (heightAGL <= zc[0]) return { k0: 0, k1: 0, t: 0 };
  if (heightAGL >= zc[nz - 1]) return { k0: nz - 1, k1: nz - 1, t: 0 };
  let k = 0;
  while (k < nz - 2 && zc[k + 1] < heightAGL) k++;
  const t = (heightAGL - zc[k]) / (zc[k + 1] - zc[k]);
  return { k0: k, k1: k + 1, t };
}

export const KAPPA = 0.4;

/**
 * Log-law wind profile, held constant above the boundary layer. Returns the
 * speed multiplier relative to `uStar/κ`.
 *
 * Using ζ + z0 inside the log means the profile is exactly zero at the ground,
 * which makes the ground-normal flux of the initial field vanish there without
 * any special casing. See docs/SOLVER.md §4.
 */
export function logProfile(zeta: number, z0: number, blHeight: number): number {
  const z = Math.min(Math.max(zeta, 0), Math.max(blHeight, 50));
  return Math.log((z + z0) / z0);
}

/** Friction velocity that reproduces `speed` at `refHeight` for roughness `z0`. */
export function frictionVelocity(speed: number, refHeight: number, z0: number, blHeight: number): number {
  const denom = logProfile(refHeight, z0, blHeight);
  return denom > 1e-6 ? (speed * KAPPA) / denom : 0;
}
